import React, { MouseEventHandler, useCallback, useContext, useEffect, useMemo } from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import moment from 'moment'
import TextButton from 'LuxKit/Button/TextButton'
import BusinessTravellerItemPolicyOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerItemPolicyOfferCreditsTextLink'
import { pluralizeToString } from 'lib/string/pluralize'
import noop from 'lib/function/noop'
import AccommodationPackageTilePricingArea from 'components/AccommodationKit/AccommodationPackageTilePricingArea'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import Label from 'LuxKit/Label'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import BedbankMarginText from './BedbankMargin'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { useInView } from 'react-intersection-observer'
import * as Analytics from 'analytics/analytics'
import { bedbankRateImpressionEventWithContext } from 'analytics/snowplow/events'
import CurrencyContext from 'contexts/currencyContext'
import Group from 'components/utils/Group'
import { isAgentHubMultiCartItemsEnabled } from 'agentHub/selectors/agentHubSelectors'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getSource } from 'selectors/offerSelectors'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import { SOURCE_TYPES } from 'constants/source'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBedbankOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PayLaterPill from 'components/Common/PayLaterPill'

interface Props {
  promotions?: App.BedbankPromotion;
  rate: App.BedbankRate;
  bundleRate?: App.BedbankRate;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer | App.BedbankOfferSummary;
  flightOrigin?: App.Airport;
  flightPrice?: number;
  checkIn?: string;
  checkOut?: string;
  departureTooEarly?: boolean;
  flightsEnabled?: boolean;
  ratePosition: number;
  rateListId?: string;
  roomPosition?: number;
  roomListId?: string;
  luxPlusLPPEnabled: boolean;
  userMultiItemCartEnabled: boolean;
  isFlightsSelected: boolean;
  isLoggedIn: boolean;
  onBookNow?: (
    rate: App.BedbankRate,
    ratePosition: number,
    flights?: boolean,
    keepBrowsing?: boolean
  ) => void;
  onAddToUserCartNow?: (
    rate: App.BedbankRate,
    ratePosition: number,
    flights?: boolean
  ) => void;
}

function BedbankPackageTilePricing(props: Props) {
  const {
    rate,
    bundleRate,
    promotions,
    rooms,
    offer,
    checkIn,
    checkOut,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    ratePosition,
    rateListId,
    roomPosition,
    roomListId,
    onBookNow = noop,
    onAddToUserCartNow = noop,
    luxPlusLPPEnabled,
    userMultiItemCartEnabled,
    isFlightsSelected,
    isLoggedIn,
  } = props

  const showModal = useContext(ModalContext)
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: offer.flights?.airportCode,
  })
  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice
  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch])

  const {
    propertyFees = 0,
    taxesAndFees,
  } = rate.totals
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees
  const bundleTaxesAndFees = bundleRate?.totals.taxesAndFees || taxesAndFees

  const hotelPrice = rate.totals.inclusive
  const hotelMemberPrice = rate.totals.memberInclusive ?? 0
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice
  const bundleMemberPrice = bundleRate?.totals.memberInclusive || hotelMemberPrice
  const showFlightsDealTag = flightOrigin?.code && offer.flights?.airportCode && flightsEnabled && deals.length > 0

  const departureTooFarInFuture = useMemo(() => {
    if (checkIn) {
      return moment(checkIn).diff(moment.utc(), 'days') > 330
    }
    return false
  }, [checkIn])

  const hasDates = !!checkIn
  const canAddToUserCart = hasDates && userMultiItemCartEnabled

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue: rate.value,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue: bundleRate?.value,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'total'
  const isPromoRate = rate.id === promotions?.id

  const margin = rate?.margin
  const marginAud = rate?.marginAud
  const supplier = rate?.supplier
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const showMarginInSpoofMode = useAppSelector(state => state.system.showMarginInSpoofMode)
  const displayMargin = Boolean(margin && marginAud && supplier && isSpoofed && showMarginInSpoofMode)
  const discount = useBedbankDiscount(rate?.discount, rate?.memberDiscount)
  const source = useAppSelector(state => getSource(state))

  const bundleMargin = bundleRate?.margin
  const bundleMarginAud = bundleRate?.marginAud
  const bundleSupplier = bundleRate?.supplier
  const displayBundleMargin = Boolean(bundleMargin && bundleMarginAud && bundleSupplier && isSpoofed && showMarginInSpoofMode)
  const canShowFlightBundlePrice = flightsEnabled && hasFlights && !departureTooEarly

  const currency = useContext(CurrencyContext)

  const [ref, inView] = useInView({ triggerOnce: true })

  useEffect(() => {
    if (inView && checkIn && checkOut && rateListId && roomPosition && roomListId) {
      Analytics.trackEvent(bedbankRateImpressionEventWithContext({
        ratePosition,
        rateListId,
        roomPosition,
        roomListId,
        offer,
        rate,
        price: totalPrices.totals.price,
        value: totalPrices.totals.value,
        checkIn,
        checkOut,
        occupancy: rooms,
        currency,
        isFlightBundle: false,
      }))
    }
  }, [inView, checkIn, checkOut, ratePosition, rateListId, roomPosition, roomListId, offer, rate, totalPrices.totals.price, totalPrices.totals.value, rooms, currency])

  useEffect(() => {
    if (inView && checkIn && checkOut && bundleRate && canShowFlightBundlePrice && rateListId && roomPosition && roomListId) {
      Analytics.trackEvent(bedbankRateImpressionEventWithContext({
        ratePosition,
        rateListId,
        roomPosition,
        roomListId,
        offer,
        rate: bundleRate,
        price: totalPrices.totals.flightBundlePrice,
        value: totalPrices.totals.flightBundleValue,
        checkIn,
        checkOut,
        occupancy: rooms,
        currency,
        isFlightBundle: true,
      }))
    }
  }, [inView, checkIn, checkOut, ratePosition, rateListId, roomPosition, roomListId, offer, bundleRate, totalPrices.totals.flightBundlePrice, totalPrices.totals.flightBundleValue, rooms, currency, canShowFlightBundlePrice])

  const onBookNowHandler = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const keepBrowsing: boolean = !!event.currentTarget.dataset.keepBrowsing
    const hasFlight: boolean = !!event.currentTarget.dataset.hasFlight
    onBookNow(rate, ratePosition, hasFlight, keepBrowsing)
  }, [onBookNow, rate, ratePosition])

  const onAddToUserCartHandler = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const hasFlight: boolean = !!event.currentTarget.dataset.hasFlight
    onAddToUserCartNow(rate, ratePosition, hasFlight)
  }, [onAddToUserCartNow, rate, ratePosition])

  const isMetaSearch = useMemo(() => {
    return source?.includes(SOURCE_TYPES.METASEARCH)
  }, [source])

  const onLoginForMemberSavings = useCallback(async() => {
    showModal<AccountAccessModalResult>(<AccountAccessModal />)
  }, [showModal])

  const shouldDisplayLoginForMemberSavings = !isLoggedIn && isMetaSearch

  const showHotelAndFlightsToggle = flightsEnabled
  const hideHotelPriceBlock = showHotelAndFlightsToggle && isFlightsSelected
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected
  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorBedbankOptions(rate, {
      price: totalPrices.totals.price,
      memberPrice: totalPrices.totals.memberPrice,
      propertyFees,
      propertyId: offer.property.id,
      offerId: offer.id,
    }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice,
      skip: !canShowFlightBundlePrice || hideHotelAndFlightsPriceBlock,
    }),
  ], [
    rate,
    totalPrices.totals.price,
    totalPrices.totals.memberPrice,
    propertyFees,
    offer,
    flightPrice,
    canShowFlightBundlePrice,
    hideHotelAndFlightsPriceBlock,
  ])

  const canPayLater = !!rate.paymentScheduleInfo?.supplierPaymentNumberOfDays && !hideHotelPriceBlock

  return <AccommodationPackageTilePricingArea>
    <Group direction="horizontal" gap={4}>
      {canPayLater && <PayLaterPill />}
      {discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={discount}
      />}
    </Group>

    <AccommodationPackageTilePricingBlocks>
      {!hideHotelPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="pricing"
        title={(flightsEnabled && !showHotelAndFlightsToggle) ? 'Hotel' : undefined}
        callToAction={<Group direction="vertical" gap={8}>
          {shouldDisplayLoginForMemberSavings && <TextButton size="large" kind="primary" nonInteractable onClick={onLoginForMemberSavings}>
            Login for member savings
          </TextButton>}
          {!shouldDisplayLoginForMemberSavings && <TextButton
            size="large"
            kind="primary"
            data-testid="book-button"
            onClick={onBookNowHandler}
          >
            {hasDates ? 'Book now' : 'Check availability'}
          </TextButton>}
          {canAddToUserCart && <TextButton size="large" onClick={onAddToUserCartHandler} startIcon={<LineShoppingCartIcon />} kind="secondary">
            Add to cart
          </TextButton>}
          {hasDates && isAgentHubMultiCartItemsEnabled && (
            <TextButton
              size="large"
              kind="secondary"
              data-keep-browsing
              onClick={onBookNowHandler}
            >
              Add to cart
            </TextButton>
          )}
        </Group>}
      >
        <VerticalSpacer gap={12}>
          <div>
            <PriceRowPriceCaption>
              {pluralizeToString('night', rate.nights)}{hasDates ? ' from' : ''}
            </PriceRowPriceCaption>
            <LuxPlusPriceGuard
              showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
              fallbackPrice={<PriceRowPrice
                data-testid="bedbank-package-price"
                size="M"
                price={totalPrices.totals.price}
                saleUnit={saleUnit}
              />}
            >
              <LuxPlusPriceStack
                size="M"
                price={totalPrices.totals.price}
                memberPrice={totalPrices.totals.memberPrice}
                saleUnit={saleUnit}
              />
            </LuxPlusPriceGuard>
            <div ref={ref}></div>
            {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
            {!isPromoRate && <PriceRowTaxesAndFees />}

            <PriceRowAgentHubCommission
              size="M"
              productType={offer.productType}
              offerLocations={[]}
              offerId={offer.id}
              packageId={rate.id}
              offerTotal={totalPrices.original.price}
              kind="plain"
            />

            <BusinessTravellerItemPolicyOfferCreditsTextLink
              type="exact"
              offer={offer}
              totalCost={totalPrices.original.price}
              numberOfNights={rate.nights}
              roomCount={rooms.length}
            />
            {!!discount && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={totalPrices.totals.value}
            />}
            {!isPromoRate && <OfferPriceDetailsRow
              trigger="price-row"
              triggerSize="M"
              offerPage
              offer={offer}
              rooms={rooms}
              propertyFees={propertyFees}
              taxesAndFees={taxesAndFees}
              hotelPrice={hotelPrice}
              hotelMemberPrice={hotelMemberPrice}
              showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
              duration={rate.nights}
              hasMobilePromotion={rate.mobilePromotion}
              cta={<Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="end" gap={8}>
                <TextButton size="large" kind="primary" onClick={onBookNowHandler}>
                  {hasDates ? 'Book now' : 'Check availability'}
                </TextButton>
                {canAddToUserCart && <TextButton
                  size="large"
                  onClick={onAddToUserCartHandler}
                  startIcon={<LineShoppingCartIcon />}
                  kind="secondary"
                >
                  Add to cart
                </TextButton>}
              </Group>}
            />}
            {displayMargin && <BedbankMarginText
              margin={margin!}
              marginAud={marginAud!}
              supplier={supplier!}
              type="bedbank_exact"
            />}
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={!checkIn ? 'estimate' : 'exact'}
          />
        </VerticalSpacer>
      </AccommodationPackageTilePricingBlock>}
      {flightsEnabled && flightOrigin && !hideHotelAndFlightsPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="flights-pricing"
        title={!showHotelAndFlightsToggle ? 'Hotel + flights' : undefined}
        callToAction={<Group direction="vertical" gap={8}>
          {flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && <>
            {canAddToUserCart && <TextButton
              size="large"
              data-has-flight
              data-testid="book-button"
              onClick={onAddToUserCartHandler}
              kind="primary"
            >
              Select package
            </TextButton>}
            {!canAddToUserCart && <TextButton
              size="large"
              kind="primary"
              data-testid="book-button"
              data-has-flight
              onClick={onBookNowHandler}
            >
              {hasDates ? 'Book now' : 'Check availability'}
            </TextButton>}
          </>}
          {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <TextButton
            size="large"
            data-filter="airport"
            kind="tertiary"
            onClick={onToggleFilter}
          >
            Change airport
          </TextButton>}
          {flightOrigin.code !== offer.flights?.airportCode && (departureTooEarly || departureTooFarInFuture) && <TextButton
            size="large"
            data-filter="checkin"
            kind="tertiary"
            onClick={onToggleFilter}
          >
            Change departure date
          </TextButton>}
        </Group>}
      >
        {canShowFlightBundlePrice && <VerticalSpacer gap={12}>
          <div>
            <PriceRowPriceCaption>
              {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', rate.nights)} + flights from
            </PriceRowPriceCaption>
            <LuxPlusPriceGuard
              showMemberPrice={(totalPrices.totals.flightBundleMemberPrice ?? 0) > 0 && luxPlusLPPEnabled}
              fallbackPrice={<PriceRowPrice
                size="M"
                price={totalPrices.totals.flightBundlePrice}
                saleUnit={saleUnit}
              />}
            >
              <LuxPlusPriceStack
                size="M"
                price={totalPrices.totals.flightBundlePrice}
                memberPrice={totalPrices.totals.flightBundleMemberPrice}
                saleUnit={offer.displayPricingAsPerPerson ? 'person' : 'total*'}
              />
            </LuxPlusPriceGuard>
            {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
            <PriceRowTaxesAndFees />
            <BusinessTravellerItemPolicyOfferCreditsTextLink
              type="exact"
              offer={offer}
              totalCost={totalPrices.original.flightBundlePrice}
              numberOfNights={rate.nights}
              roomCount={rooms.length}
            />
            {discount > 0 && !isPromoRate && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={totalPrices.totals.flightBundleValue}
            />}
            {showFlightsDealTag && <Label variant="critical" kind="plain" startIcon={<SolidFireIcon />}>Flight deal available</Label>}
            {!isPromoRate && <OfferPriceDetailsRow
              trigger="price-row"
              triggerSize="M"
              offerPage
              offer={offer}
              rooms={rooms}
              propertyFees={bundlePropertyFees}
              taxesAndFees={bundleTaxesAndFees}
              hotelPrice={bundlePrice}
              hotelMemberPrice={bundleMemberPrice}
              showMemberPrice={luxPlusLPPEnabled && bundleMemberPrice > 0}
              flightPrice={flightPrice}
              duration={rate.nights}
              cta={<Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="end" gap={8}>
                {canAddToUserCart && <TextButton
                  size="large"
                  onClick={onAddToUserCartHandler}
                  data-has-flight
                  kind="primary"
                >
                  Select package
                </TextButton>}
                {(!userMultiItemCartEnabled || !hasDates) && <TextButton size="large" kind="primary" onClick={onBookNowHandler}>
                  {hasDates ? 'Book now' : 'Check availability'}
                </TextButton>}
              </Group>}
            />}
            {displayBundleMargin && <BedbankMarginText
              margin={bundleMargin!}
              marginAud={bundleMarginAud!}
              supplier={bundleSupplier!}
              type="bedbank_exact_accom"
            />}
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
          />
        </VerticalSpacer>}
        <AtolProtectedTooltip />
      </AccommodationPackageTilePricingBlock>}
    </AccommodationPackageTilePricingBlocks>
  </AccommodationPackageTilePricingArea>
}

export default withOfferPageAndReduxState((state, appState) => ({
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
  isLoggedIn: !!appState.auth.account.memberId,
  isFlightsSelected: state.flights,
}), BedbankPackageTilePricing)
